import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useClassTypesQuery,
  useInstructorsQuery,
  useRoomsQuery,
} from "@/modules/master/presentation/api/queries";
import type { BookingCalendarDTO } from "@/modules/private-lessons/domain/contracts/bookings";
import {
  formatPrivateLessonBookingPlace,
  formatPrivateLessonCalendarTitle,
  mapBookingCalendarToTimetableItem,
} from "@/modules/private-lessons/domain/lib/booking-display";
import { useBookingsCalendarQuery } from "@/modules/private-lessons/presentation/api/queries";
import { CreatePrivateBookingDialog } from "@/modules/private-lessons/presentation/ui/CreatePrivateBookingDialog";
import { EditPrivateBookingDialog } from "@/modules/private-lessons/presentation/ui/EditPrivateBookingDialog";
import type { TimetableItem } from "@/modules/public-booking/domain/contracts/public-booking";
import { PublicTimetable } from "@/modules/public-booking/presentation/ui/PublicTimetable";
import type { RoomBookingCalendarDTO } from "@/modules/room-bookings/domain/contracts/room-bookings";
import { useRoomBookingsCalendarQuery } from "@/modules/room-bookings/presentation/api/queries";
import { CreateRoomBookingDialog } from "@/modules/room-bookings/presentation/ui/CreateRoomBookingDialog";
import { EditRoomBookingDialog } from "@/modules/room-bookings/presentation/ui/EditRoomBookingDialog";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Button,
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { endOfWeek, format, startOfWeek } from "date-fns";
import {
  CalendarDaysIcon,
  DownloadIcon,
  FilterIcon,
  MapPinIcon,
  PlusIcon,
  RefreshCwIcon,
  UserIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import type { ClassSessionDTO } from "../../domain/contracts/sessions";
import {
  useCalendarSessionsQuery,
  useCheckConflictsQuery,
  useClassTemplatesQuery,
} from "../api/queries";
import { CreateSessionDialog } from "../ui/CreateSessionDialog";
import { EditSessionDialog } from "../ui/EditSessionDialog";
import { MoveSessionDialog } from "../ui/MoveSessionDialog";
import {
  CLASS_SESSION_OFF_SITE_TIMETABLE_ROOM_ID,
  formatClassSessionCalendarTitle,
} from "../ui/session-display";

/** Map ClassSessionDTO to TimetableItem for PublicTimetable */
function sessionToTimetableItem(s: ClassSessionDTO): TimetableItem {
  const isOff = s.locationKind === "off_site";
  return {
    type: "class",
    id: s.id,
    roomId: isOff ? CLASS_SESSION_OFF_SITE_TIMETABLE_ROOM_ID : (s.roomId ?? ""),
    roomName: s.roomName || s.offSiteLabel || "",
    date: s.date,
    startTime: s.startTime,
    endTime: s.endTime,
    title: formatClassSessionCalendarTitle(s),
    status: s.status,
  };
}

/** Map RoomBookingCalendarDTO to TimetableItem */
function roomBookingToTimetableItem(r: RoomBookingCalendarDTO): TimetableItem {
  return {
    type: "room_booking",
    id: r.id,
    roomId: r.roomId,
    roomName: r.roomName ?? "",
    date: r.date,
    startTime: r.startTime,
    endTime: r.endTime,
    title: r.title ?? r.contactName ?? "ຈອງຫ້ອງ",
    status: r.status,
  };
}

function toYMD(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

/** Escape CSV field (wrap in quotes, double internal quotes) */
function csvEscape(s: string): string {
  return `"${String(s).replaceAll('"', '""')}"`;
}

/** Build CSV rows for calendar export: class ຊົ່ວໂມງຮຽນ + private lessons + room bookings */
function buildCalendarCsv(
  sessions: ClassSessionDTO[],
  bookings: BookingCalendarDTO[],
  roomBookings: RoomBookingCalendarDTO[],
): string {
  const header =
    "type,date,startTime,endTime,locationKind,offSiteLabel,engagementKind,roomName,status,title,participants,capacity,attendanceCount";
  const rows: string[] = [];

  for (const s of sessions) {
    const title = s.templateName ?? "";
    const participants = Array.isArray(s.instructorNames)
      ? s.instructorNames.join(", ")
      : "";
    rows.push(
      [
        "class",
        s.date,
        s.startTime,
        s.endTime,
        s.locationKind ?? "studio",
        csvEscape(s.offSiteLabel ?? ""),
        s.engagementKind ?? "standard",
        csvEscape(s.roomName || ""),
        s.status,
        csvEscape(title),
        csvEscape(participants),
        String(s.capacity ?? 0),
        String(s.attendanceCount ?? 0),
      ].join(","),
    );
  }

  for (const b of bookings) {
    const title = formatPrivateLessonCalendarTitle(b);
    const instructorStr = Array.isArray(b.instructorNames)
      ? b.instructorNames.join(", ")
      : "";
    const participants = instructorStr
      ? `${b.studentName} | ຄູ: ${instructorStr}`
      : b.studentName;
    rows.push(
      [
        "private",
        b.date,
        b.startTime,
        b.endTime,
        b.locationKind ?? "studio",
        csvEscape(b.offSiteLabel ?? ""),
        b.engagementKind ?? "standard",
        csvEscape(formatPrivateLessonBookingPlace(b)),
        b.status,
        csvEscape(title),
        csvEscape(participants),
        "",
        "",
      ].join(","),
    );
  }

  for (const rb of roomBookings) {
    const title = rb.title || rb.contactName || "ຈອງຫ້ອງ";
    const participants = rb.contactName ?? "";
    rows.push(
      [
        "room_booking",
        rb.date,
        rb.startTime,
        rb.endTime,
        "",
        "",
        "",
        csvEscape(rb.roomName ?? ""),
        rb.status,
        csvEscape(title),
        csvEscape(participants),
        "",
        "",
      ].join(","),
    );
  }

  return [header, ...rows].join("\n");
}

type CalendarSlot = {
  id: string;
  sessionType: "class" | "private" | "room_booking";
  roomId: string;
  date: string;
  startTime: string;
  endTime: string;
};

function computeRoomConflictIds(slots: CalendarSlot[]): Set<string> {
  const ids = new Set<string>();
  for (let i = 0; i < slots.length; i++) {
    const a = slots[i];
    if (!a) continue;
    const aStart = new Date(`${a.date}T${a.startTime}`).getTime();
    const aEnd = new Date(`${a.date}T${a.endTime}`).getTime();
    const aKey = `${a.sessionType}-${a.id}`;
    for (let j = i + 1; j < slots.length; j++) {
      const b = slots[j];
      if (!b || a.roomId !== b.roomId) continue;
      const bStart = new Date(`${b.date}T${b.startTime}`).getTime();
      const bEnd = new Date(`${b.date}T${b.endTime}`).getTime();
      if (aStart < bEnd && aEnd > bStart) {
        ids.add(aKey);
        ids.add(`${b.sessionType}-${b.id}`);
      }
    }
  }
  return ids;
}

export function ScheduleCalendarPage() {
  const canCreate = useActionPermission(["classes:create"]);
  const canRead = useActionPermission(["classes:read"]);
  const canCreatePrivate = useActionPermission(["private-lessons:create"]);
  const canReadPrivateLessons = useActionPermission(["private-lessons:read"]);
  const canCreateRoom = useActionPermission(["room-bookings:create"]);
  const canReadRoomBookings = useActionPermission(["room-bookings:read"]);

  const canCreateAny = canCreate || canCreatePrivate || canCreateRoom;

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 0 });
  const [rangeStart, setRangeStart] = useState(() => toYMD(weekStart));
  const [rangeEnd, setRangeEnd] = useState(() =>
    toYMD(endOfWeek(now, { weekStartsOn: 0 })),
  );
  const [createTypePickerOpen, setCreateTypePickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [slotInitial, setSlotInitial] = useState<{
    date: string;
    startTime: string;
    endTime: string;
  } | null>(null);
  const [createPrivateOpen, setCreatePrivateOpen] = useState(false);
  const [createRoomOpen, setCreateRoomOpen] = useState(false);
  const [editSession, setEditSession] = useState<ClassSessionDTO | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editPrivateBookingId, setEditPrivateBookingId] = useState<
    string | null
  >(null);
  const [editPrivateOpen, setEditPrivateOpen] = useState(false);
  const [editRoomBookingId, setEditRoomBookingId] = useState<string | null>(
    null,
  );
  const [editRoomOpen, setEditRoomOpen] = useState(false);
  const [moveSession, setMoveSession] = useState<ClassSessionDTO | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);

  const [filterRoomId, setFilterRoomId] = useState<string>("");
  const [filterInstructorId, setFilterInstructorId] = useState<string>("");
  const [filterClassTypeId, setFilterClassTypeId] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterTemplateId, setFilterTemplateId] = useState<string>("");
  type ResourceViewMode = "default" | "room" | "instructor";
  const [resourceViewMode, setResourceViewMode] =
    useState<ResourceViewMode>("default");

  const calendarFilters = useMemo(() => {
    const f: {
      roomId?: string;
      instructorId?: string;
      classTypeId?: string;
      status?: string;
      templateId?: string;
    } = {};
    if (filterRoomId) f.roomId = filterRoomId;
    if (filterInstructorId) f.instructorId = filterInstructorId;
    if (filterClassTypeId) f.classTypeId = filterClassTypeId;
    if (filterStatus) f.status = filterStatus;
    if (filterTemplateId) f.templateId = filterTemplateId;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [
    filterRoomId,
    filterInstructorId,
    filterClassTypeId,
    filterStatus,
    filterTemplateId,
  ]);

  const bookingFilters = useMemo(() => {
    const f: { roomId?: string; instructorId?: string; status?: string } = {};
    if (filterRoomId) f.roomId = filterRoomId;
    if (filterInstructorId) f.instructorId = filterInstructorId;
    if (filterStatus) f.status = filterStatus;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [filterRoomId, filterInstructorId, filterStatus]);

  const roomBookingFilters = useMemo(() => {
    const f: { roomId?: string; status?: string } = {};
    if (filterRoomId) f.roomId = filterRoomId;
    if (filterStatus) f.status = filterStatus;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [filterRoomId, filterStatus]);

  const { data, isLoading, refetch } = useCalendarSessionsQuery(
    rangeStart,
    rangeEnd,
    calendarFilters,
  );

  const { data: roomsData } = useRoomsQuery({ limit: 200, offset: 0 });
  const { data: instructorsData } = useInstructorsQuery({
    limit: 200,
    offset: 0,
  });
  const { data: classTypesData } = useClassTypesQuery({
    limit: 100,
    offset: 0,
  });
  const { data: templatesData } = useClassTemplatesQuery({
    limit: 100,
    offset: 0,
  });
  const rooms = roomsData?.data ?? [];
  const instructors = instructorsData?.data ?? [];
  const classTypes = classTypesData?.data ?? [];
  const templates = templatesData?.data ?? [];
  const { data: bookingsData, refetch: refetchBookings } =
    useBookingsCalendarQuery(rangeStart, rangeEnd, bookingFilters, {
      enabled: canReadPrivateLessons,
    });
  const { data: roomBookingsData, refetch: refetchRoomBookings } =
    useRoomBookingsCalendarQuery(rangeStart, rangeEnd, roomBookingFilters, {
      enabled: canReadRoomBookings,
    });
  const sessions = data?.data ?? [];
  const bookings = bookingsData?.data ?? [];
  const roomBookings = roomBookingsData?.data ?? [];

  const timetableItems = useMemo(() => {
    const classItems = sessions.map(sessionToTimetableItem);
    const privateItems = bookings.map((b) =>
      mapBookingCalendarToTimetableItem(b, CLASS_SESSION_OFF_SITE_TIMETABLE_ROOM_ID),
    );
    const roomItems = roomBookings.map(roomBookingToTimetableItem);
    return [...classItems, ...privateItems, ...roomItems];
  }, [sessions, bookings, roomBookings]);

  const publicRooms = useMemo(
    () => [
      { id: CLASS_SESSION_OFF_SITE_TIMETABLE_ROOM_ID, name: "ນອກສະຖານທີ່" },
      ...rooms.map((r) => ({ id: r.id, name: r.name })),
    ],
    [rooms],
  );

  const allSlots: CalendarSlot[] = useMemo(() => {
    const classSlots: CalendarSlot[] = sessions.map((s) => {
      const roomKey =
        s.locationKind === "off_site"
          ? `off-site-class-${s.id}`
          : (s.roomId ?? "");
      return {
        id: s.id,
        sessionType: "class" as const,
        roomId: roomKey,
        date: s.date,
        startTime: s.startTime,
        endTime: s.endTime,
      };
    });
    const privateSlots: CalendarSlot[] = bookings.map((b) => ({
      id: b.id,
      sessionType: "private" as const,
      roomId:
        b.locationKind === "off_site"
          ? CLASS_SESSION_OFF_SITE_TIMETABLE_ROOM_ID
          : (b.roomId ?? ""),
      date: b.date,
      startTime: b.startTime,
      endTime: b.endTime,
    }));
    const roomBookingSlots: CalendarSlot[] = roomBookings.map((b) => ({
      id: b.id,
      sessionType: "room_booking" as const,
      roomId: b.roomId,
      date: b.date,
      startTime: b.startTime,
      endTime: b.endTime,
    }));
    return [...classSlots, ...privateSlots, ...roomBookingSlots];
  }, [sessions, bookings, roomBookings]);

  const { data: conflictsData } = useCheckConflictsQuery(
    rangeStart,
    rangeEnd,
    calendarFilters?.roomId,
  );
  const conflictIds = useMemo(() => {
    const fromApi = new Set<string>();
    if (conflictsData?.conflicts) {
      for (const c of conflictsData.conflicts) {
        fromApi.add(`${c.type}-${c.id}`);
      }
    }
    if (fromApi.size > 0) return fromApi;
    return computeRoomConflictIds(allSlots);
  }, [conflictsData, allSlots]);

  const handleRangeChange = useCallback((start: string, end: string) => {
    setRangeStart(start);
    setRangeEnd(end);
  }, []);

  const handleSlotSelect = useCallback(
    (slot: { date: string; startTime: string; endTime: string }) => {
      if (!canCreateAny) return;
      const startTime =
        slot.startTime.length === 5 ? `${slot.startTime}:00` : slot.startTime;
      const endTime =
        slot.endTime.length === 5 ? `${slot.endTime}:00` : slot.endTime;
      setSlotInitial({
        date: slot.date,
        startTime,
        endTime,
      });
      setCreateTypePickerOpen(true);
    },
    [canCreateAny],
  );

  const handleEventClick = useCallback(
    (item: TimetableItem) => {
      if (item.type === "class") {
        if (canRead) {
          const session = sessions.find((s) => s.id === item.id);
          if (session) {
            setEditSession(session);
            setEditOpen(true);
          }
        }
      } else if (item.type === "room_booking") {
        if (canReadRoomBookings) {
          setEditRoomBookingId(item.id);
          setEditRoomOpen(true);
        }
      } else if (item.type === "private") {
        if (canReadPrivateLessons) {
          setEditPrivateBookingId(item.id);
          setEditPrivateOpen(true);
        }
      }
    },
    [canRead, canReadPrivateLessons, canReadRoomBookings, sessions],
  );

  const handleEditSuccess = useCallback(() => {
    setEditOpen(false);
    setEditSession(null);
    refetch();
    refetchBookings();
    refetchRoomBookings();
  }, [refetch, refetchBookings, refetchRoomBookings]);

  const handleOpenMove = useCallback(() => {
    if (editSession) {
      setMoveSession(editSession);
      setMoveOpen(true);
    }
  }, [editSession]);

  const handleMoveSuccess = useCallback(() => {
    setMoveOpen(false);
    setMoveSession(null);
    setEditOpen(false);
    setEditSession(null);
    refetch();
    refetchBookings();
    refetchRoomBookings();
  }, [refetch, refetchBookings, refetchRoomBookings]);

  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(() => {
    setExporting(true);
    try {
      const csv = buildCalendarCsv(sessions, bookings, roomBookings);
      const blob = new Blob([csv], {
        type: "text/csv; charset=utf-8",
      });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `schedule-${rangeStart}-${rangeEnd}.csv`;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setExporting(false);
    }
  }, [sessions, bookings, roomBookings, rangeStart, rangeEnd]);

  const handleCreateSuccess = useCallback(() => {
    setCreateOpen(false);
    setCreatePrivateOpen(false);
    setCreateRoomOpen(false);
    setCreateTypePickerOpen(false);
    setSlotInitial(null);
    refetch();
    refetchBookings();
    refetchRoomBookings();
  }, [refetch, refetchBookings, refetchRoomBookings]);

  const handleEditPrivateSuccess = useCallback(() => {
    setEditPrivateOpen(false);
    setEditPrivateBookingId(null);
    refetch();
    refetchBookings();
    refetchRoomBookings();
  }, [refetch, refetchBookings, refetchRoomBookings]);

  const handleEditRoomSuccess = useCallback(() => {
    setEditRoomOpen(false);
    setEditRoomBookingId(null);
    refetch();
    refetchBookings();
    refetchRoomBookings();
  }, [refetch, refetchBookings, refetchRoomBookings]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຕາຕະລາງ" },
          ]}
        />
      </Header>
      <Main>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="font-bold text-3xl tracking-tight">ຕາຕະລາງຄລາດສ໌</h1>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  refetch();
                  refetchBookings();
                  refetchRoomBookings();
                }}
                disabled={isLoading}
              >
                <RefreshCwIcon className="mr-2 size-4" />
                ໂຫຼດໃໝ່
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
              >
                <DownloadIcon className="mr-2 size-4" />
                {exporting ? "ກຳລັງສົ່ງ..." : "Export"}
              </Button>
              {canCreateAny && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setSlotInitial(null);
                    setCreateTypePickerOpen(true);
                  }}
                  className="bg-primary text-primary-foreground"
                >
                  <PlusIcon className="mr-2 size-4" />
                  ເພີ່ມ
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-3">
            <FilterIcon className="size-4 text-muted-foreground" />
            <div className="flex flex-wrap items-center gap-4">
              <div className="space-y-1">
                <Label className="text-xs">ຫ້ອງ</Label>
                <Select
                  value={filterRoomId || "all"}
                  onValueChange={(v) => setFilterRoomId(v === "all" ? "" : v)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="ທຸກຫ້ອງ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ທຸກຫ້ອງ</SelectItem>
                    {rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">ຄູຜູ້ສອນ</Label>
                <Select
                  value={filterInstructorId || "all"}
                  onValueChange={(v) =>
                    setFilterInstructorId(v === "all" ? "" : v)
                  }
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="ທຸກຄູ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ທຸກຄູ</SelectItem>
                    {instructors.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">ປະເພດຄລາດສ໌</Label>
                <Select
                  value={filterClassTypeId || "all"}
                  onValueChange={(v) =>
                    setFilterClassTypeId(v === "all" ? "" : v)
                  }
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="ທຸກປະເພດ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ທຸກປະເພດ</SelectItem>
                    {classTypes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">ສະຖານະ</Label>
                <Select
                  value={filterStatus || "all"}
                  onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="ທຸກສະຖານະ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ທຸກສະຖານະ</SelectItem>
                    <SelectItem value="scheduled">ຈອງແລ້ວ</SelectItem>
                    <SelectItem value="completed">ສຳເລັດ</SelectItem>
                    <SelectItem value="cancelled">ຍົກເລີກ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">ແມ່ແບບ</Label>
                <Select
                  value={filterTemplateId || "all"}
                  onValueChange={(v) =>
                    setFilterTemplateId(v === "all" ? "" : v)
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="ທຸກ template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ທຸກ template</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.classTypeName} - {t.levelName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">ແບບສະແດງ</Label>
                <Select
                  value={resourceViewMode}
                  onValueChange={(v) =>
                    setResourceViewMode(v as ResourceViewMode)
                  }
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">ຕາຕະລາງປົກກະຕິ</SelectItem>
                    <SelectItem value="room">ແຍກຕາມຫ້ອງ</SelectItem>
                    <SelectItem value="instructor">ແຍກຕາມຄູ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="min-h-[700px] rounded-lg border bg-card p-4">
            <PublicTimetable
              items={timetableItems}
              rooms={publicRooms}
              onSlotSelect={handleSlotSelect}
              onEventClick={handleEventClick}
              conflictIds={conflictIds}
              onRangeChange={handleRangeChange}
              initialView="week"
              initialDate={new Date()}
            />
          </div>
        </div>
      </Main>

      <Dialog
        open={createTypePickerOpen}
        onOpenChange={setCreateTypePickerOpen}
      >
        <DialogPortal>
          <DialogOverlay />
          <DialogContent className="w-[min(95vw,320px)]" showCloseButton>
            <div className="flex flex-col gap-2">
              {canCreate && (
                <Button
                  variant="outline"
                  className="justify-start gap-2"
                  onClick={() => {
                    setCreateTypePickerOpen(false);
                    setCreateOpen(true);
                  }}
                >
                  <CalendarDaysIcon className="size-4" />
                  ຄລາດສ໌
                </Button>
              )}
              {canCreatePrivate && (
                <Button
                  variant="outline"
                  className="justify-start gap-2"
                  onClick={() => {
                    setCreateTypePickerOpen(false);
                    setCreatePrivateOpen(true);
                  }}
                >
                  <UserIcon className="size-4" />
                  ວິຊາສ່ວນຕົວ
                </Button>
              )}
              {canCreateRoom && (
                <Button
                  variant="outline"
                  className="justify-start gap-2"
                  onClick={() => {
                    setCreateTypePickerOpen(false);
                    setCreateRoomOpen(true);
                  }}
                >
                  <MapPinIcon className="size-4" />
                  ຈອງຫ້ອງ
                </Button>
              )}
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <CreateSessionDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialDate={slotInitial?.date}
        initialStartTime={slotInitial?.startTime}
        initialEndTime={slotInitial?.endTime}
        onSuccess={handleCreateSuccess}
      />

      <CreatePrivateBookingDialog
        open={createPrivateOpen}
        onOpenChange={setCreatePrivateOpen}
        initialDate={slotInitial?.date}
        initialStartTime={slotInitial?.startTime}
        initialEndTime={slotInitial?.endTime}
        onSuccess={handleCreateSuccess}
      />

      <CreateRoomBookingDialog
        open={createRoomOpen}
        onOpenChange={setCreateRoomOpen}
        initialDate={slotInitial?.date}
        initialStartTime={slotInitial?.startTime}
        initialEndTime={slotInitial?.endTime}
        onSuccess={handleCreateSuccess}
      />

      {editSession && (
        <EditSessionDialog
          session={editSession}
          open={editOpen}
          onOpenChange={(open) => {
            setEditOpen(open);
            if (!open) setEditSession(null);
          }}
          onSuccess={handleEditSuccess}
          onOpenMove={handleOpenMove}
        />
      )}

      <EditPrivateBookingDialog
        bookingId={editPrivateBookingId}
        open={editPrivateOpen}
        onOpenChange={(open) => {
          setEditPrivateOpen(open);
          if (!open) setEditPrivateBookingId(null);
        }}
        onSuccess={handleEditPrivateSuccess}
      />

      <EditRoomBookingDialog
        roomBookingId={editRoomBookingId}
        open={editRoomOpen}
        onOpenChange={(open) => {
          setEditRoomOpen(open);
          if (!open) setEditRoomBookingId(null);
        }}
        onSuccess={handleEditRoomSuccess}
      />

      {moveSession && (
        <MoveSessionDialog
          session={moveSession}
          open={moveOpen}
          onOpenChange={(open) => {
            setMoveOpen(open);
            if (!open) setMoveSession(null);
          }}
          onSuccess={handleMoveSuccess}
        />
      )}
    </>
  );
}
